import type {
  AreaField,
  AttributeField,
  InputField,
  MultiSelectField,
  PhotosField as PhotosFieldType,
  SelectField,
} from "@/features/properties/types/propertyAttributes";

export type LeafField = InputField | SelectField | PhotosFieldType | AreaField | MultiSelectField;

export function getFieldAnchorId(field: Pick<AttributeField, "id">) {
  return `site-attribute-field-${field.id}`;
}

export function getCategoryAnchorId(field: AttributeField) {
  const source = field.ref_id || field.id || field.title;
  return `site-attribute-${source
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")}`;
}

// Returns the searchable text of a leaf field's value (titles are matched
// separately). Photos are skipped and area HTML is stripped of tags.
function getSearchableValue(field: LeafField): string {
  switch (field.type) {
    case "multiSelect":
      return Array.isArray(field.value) ? field.value.join(" ") : String(field.value ?? "");
    case "photos":
      return "";
    case "area":
      return (field.value ?? "").replace(/<[^>]*>/g, " ");
    default:
      return field.value == null ? "" : String(field.value);
  }
}

// Matches a field (and its descendants) against a lowercased query, checking
// both titles and leaf values so the search finds categories by their content.
export function fieldMatchesQuery(field: AttributeField, query: string): boolean {
  if ((field.title ?? "").toLowerCase().includes(query)) {
    return true;
  }
  if (field.type === "node") {
    return field.fields.some((child) => fieldMatchesQuery(child, query));
  }
  return getSearchableValue(field as LeafField).toLowerCase().includes(query);
}

export function cleanFieldTitle(title: string) {
  return title.replace(/\s*\[.*?\]\s*$/, "");
}

export function getFieldEvidenceUrls(field: LeafField): string[] {
  const urls: string[] = [];
  if (Array.isArray(field.evidence)) {
    urls.push(...field.evidence.filter((url) => typeof url === "string" && url.trim() !== ""));
  }
  if (field.type === "photos" && Array.isArray(field.value)) {
    urls.push(...field.value.filter((url) => typeof url === "string" && url.trim() !== ""));
  }
  return Array.from(new Set(urls));
}

// Unanswered questions show "PROCESSING" (still being reviewed/captured); a real
// answer is shown as-is, including a genuine "N/A" the surveyor entered.
export function getFieldAnswerText(field: LeafField): string {
  switch (field.type) {
    case "photos":
      return Array.isArray(field.value) && field.value.length > 0
        ? `${field.value.length} photo${field.value.length === 1 ? "" : "s"} attached`
        : "PROCESSING";
    case "multiSelect":
      // Empty array = unanswered (note: [] is truthy, so it must be handled
      // before the String() fallback or it would render as "").
      if (Array.isArray(field.value)) {
        return field.value.length > 0 ? field.value.join(", ") : "PROCESSING";
      }
      return field.value ? String(field.value) : "PROCESSING";
    case "area": {
      const text = typeof field.value === "string"
        ? field.value.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim()
        : field.value == null ? "" : String(field.value);
      return text === "" ? "PROCESSING" : text;
    }
    default: {
      const text = field.value == null ? "" : String(field.value).trim();
      return text === "" ? "PROCESSING" : text;
    }
  }
}
